import { useState, useEffect, useRef, useCallback } from 'react';

export type AnimationStates = 'BEFORE_OPENING' | 'OPENING' | 'OPEN' | 'BEFORE_CLOSING' | 'CLOSING' | 'CLOSED';

/**
 * Used to manage the animation state of the accordion.
 */
const useAccordionAnimationState = (duration: number) => {
    const [animationState, setAnimationState] = useState<AnimationStates>('CLOSED');

    // Ref to store and function to clear all timeouts.
    const timeouts = useRef<NodeJS.Timeout[]>([]);
    const clearTimeouts = () => {
        timeouts.current.forEach((timeout) => {
            clearTimeout(timeout);
        });
        timeouts.current = [];
    };

    const startOpenAnimation = useCallback(() => {
        clearTimeouts();
        setAnimationState('BEFORE_OPENING');
    }, []);

    const startCloseAnimation = useCallback(() => {
        clearTimeouts();
        setAnimationState('BEFORE_CLOSING');
    }, []);

    useEffect(() => {
        if (animationState === 'BEFORE_OPENING') {
            setAnimationState('OPENING');
            timeouts.current.push(setTimeout(() => setAnimationState('OPEN'), duration));
            return;
        }

        if (animationState === 'BEFORE_CLOSING') {
            timeouts.current.push(
                setTimeout(() => {
                    setAnimationState('CLOSING');
                    timeouts.current.push(setTimeout(() => setAnimationState('CLOSED'), duration));
                }, 50)
            ); // Need a slight delay for CSS to be applied.
        }
    }, [animationState, duration]);

    return {
        startOpenAnimation,
        startCloseAnimation,
        animationState,
    };
};

export default useAccordionAnimationState;
